<template>
  <MTag
    v-if="!asInput && !textOnly && !coloredText && !asTag"
    ref="tagTriggerRef"
    role="button"
    :tabindex="disabled ? '-1' : '0'"
    :closable="false"
    class="items-center dropdown-pill-trigger"
    :class="{
      disabled: disabled,
      'cursor-pointer': !disabled,
      'text-ellipsis': true,
    }"
    :style="{ maxWidth: '180px', display: 'inline-flex' }"
    :variant="undefined"
    :title="
      selectedItem
        ? selectedItem.text || selectedItem.name || selectedItem.label
        : placeholder
    "
    @keydown="handleClickTagButton"
  >
    <slot name="prefix-text" :item="selectedItem" />
    <FlotoDot
      v-if="selectedItem && selectedItem.color"
      :bg="selectedItem.color"
    />
    <span class="text-ellipsis">
      {{
        selectedItem
          ? selectedItem.text || selectedItem.name || selectedItem.label
          : placeholder
      }}
    </span>
    <slot name="suffix-text" />
    <MIcon name="chevron-down-custom" class="action-icon" size="sm" />
  </MTag>
  <span
    v-else-if="textOnly"
    class="truncate-text"
    :title="
      selectedItem
        ? selectedItem.text || selectedItem.name || selectedItem.label
        : ''
    "
  >
    {{
      selectedItem
        ? selectedItem.text || selectedItem.name || selectedItem.label
        : placeholder
    }}
  </span>
  <span
    v-else-if="coloredText"
    class="truncate-text"
    :style="{ color: selectedItem.color }"
    :title="
      selectedItem
        ? selectedItem.text || selectedItem.name || selectedItem.label
        : ''
    "
  >
    {{
      selectedItem
        ? selectedItem.text || selectedItem.name || selectedItem.label
        : placeholder
    }}
  </span>
  <span v-else-if="asTag">
    <MTag :closable="false" class="cursor-default" :style="currentStatusStyle">
      {{ selectedItem.text || selectedItem.name || selectedItem.label }}
    </MTag>
  </span>

  <div
    v-else
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click.prevent.stop="$emit('click')"
  >
    <MInput
      ref="inputBox"
      read-only
      :disabled="disabled"
      :size="size"
      :value="
        selectedItem
          ? selectedItem.text || selectedItem.name || selectedItem.label
          : undefined
      "
      :placeholder="placeholder"
      class="dropdown-trigger-input"
      :class="[{ 'dropdown-open': isOpen }, inputClasses]"
      :aria-label="ariaLabel || placeholder || $t('select')"
      @focus="handleFocus"
      @keydown="handleKeyDown"
    >
      <FlotoDot
        v-if="selectedItem && selectedItem.color"
        slot="prefix"
        :bg="selectedItem.color"
      />
      <MIcon
        v-if="isActiveHover && allowClear && !disabled"
        slot="suffix"
        type="fas"
        name="times-circle"
        size="sm"
        class="cursor-pointer"
        @click.stop="$emit('reset', undefined)"
      />
      <MIcon
        v-else
        slot="suffix"
        type="fal"
        name="chevron-down"
        size="sm"
        class="cursor-pointer text-neutral-light"
      />
    </MInput>
    <slot name="bellow-input" />
  </div>
</template>

<script>
import { PreferenceComputed } from '@state/modules/preference'
import { getTagStyleFromColor } from '@utils/color'
import { keyEvents } from '@utils/keyevents'

export default {
  name: 'DropdownTrigger',
  props: {
    selectedItem: { type: [Object], default: undefined },
    allowClear: { type: Boolean, default: false },
    textOnly: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    toggle: { type: Function, required: true },
    asInput: { type: Boolean, default: false },
    asTag: { type: Boolean, default: false },
    coloredText: { type: Boolean, default: false },
    // eslint-disable-next-line
    size: { type: String, default: undefined },
    placeholder: { type: String, default: undefined },
    isOpen: { type: Boolean, default: false },
    inputClasses: { type: [String, Object, Array], default: undefined },
    focusEventBrodcast: { type: Boolean, default: false },
    ariaLabel: { type: String, default: undefined },
  },
  data() {
    return {
      isActiveHover: false,
      isHovering: false,
      currentStyle: {},
    }
  },
  computed: {
    ...PreferenceComputed,
    color() {
      if (this.selectedItem) {
        return this.selectedItem.color
      }
      return undefined
    },
    currentStatusStyle() {
      return getTagStyleFromColor(this.color, this.theme) || {}
    },
  },
  watch: {
    selectedItem: {
      immediate: true,
      handler() {
        if (this.selectedItem) {
          this.updateColor()
        }
      },
    },
  },
  methods: {
    handleKeyDown($event) {
      const inputBox = this.$refs.inputBox

      // Check if it's a KeyboardEvent and handle spacebar and backspace
      if ($event instanceof KeyboardEvent) {
        // Prevent spacebar behavior if search box is empty
        // if (!inputBox.value) {
        if ($event.key === keyEvents.SPACE) {
          // Prevent space from being typed
          $event.preventDefault()
          // Open the dropdown
          if (inputBox) {
            inputBox.$el.click()
          }
        }
      }
    },
    handleClickTagButton($event) {
      const buttonRef = this.$refs['tagTriggerRef']
      if ($event instanceof KeyboardEvent) {
        if ($event.key === keyEvents.SPACE) {
          $event.preventDefault()
          if (buttonRef) {
            buttonRef.$el.click()
          }
        }
      }
    },
    updateColor() {
      setTimeout(() => {
        this.currentStyle = getTagStyleFromColor(
          this.selectedItem.color,
          this.theme
        )
      }, 0)
    },
    handleMouseEnter() {
      this.isHovering = true
      if (this.selectedItem) {
        this.isActiveHover = true
      }
    },
    handleMouseLeave() {
      this.isActiveHover = false
      this.isHovering = false
    },
    handleFocus(e) {
      if (this.focusEventBrodcast && !this.isHovering) {
        e.target.click()
        // this.toggle()
      }
    },
  },
}
</script>

<style lang="less" scoped>
.truncate-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
